import React, { useState } from 'react';
import { Row, Col, Card, CardBody, Label } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import ActionButton from '../../../../../Components/Common/ActionButton';

import * as api from '../procurementApi';
import { validateValidity } from '../procurementValidation';
import { STEP, STEP_STATUS } from '../procurementConstants';
import { formatDateTime } from '../../formatters';

// "Jadwal Batas Penawaran & Masa Berlaku" — dulu tab VALIDITY tersendiri, kini
// kartu di ATAS tab Main Requirement (mengikuti desain acuan) dengan tombol
// simpan sendiri. Tombol memanggil SaveValidity dengan stepStatus Completed
// (setelah lolos validateValidity) dan menandai step `validity` selesai, jadi
// gating Submit di Overview tetap bekerja tanpa tab terpisah.
const ProcurementValidityTab = ({
  procurementValidityId,
  detail,
  setDetail,
  readOnly = false,
}) => {
  const validity = detail.validity || {};
  const [saving, setSaving] = useState(false);

  const patch = next =>
    setDetail(prev => ({
      ...prev,
      validity: { ...prev.validity, ...next },
    }));

  const toIso = d => (d ? d.toISOString() : null);

  const handleSaveSchedule = async () => {
    const errors = validateValidity(detail);
    if (errors.length) {
      toast.error(
        <div>
          Please fix the following:
          <ul className='mb-0 mt-1' style={{ paddingLeft: '1.25rem' }}>
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>,
        { autoClose: 6000 }
      );
      return;
    }
    setSaving(true);
    try {
      await api.saveValidity(procurementValidityId, {
        stepStatus: STEP_STATUS.COMPLETED,
        submissionDeadline: validity.submissionDeadline || null,
        validityPeriod: validity.validityPeriod || null,
      });
      // Tandai step validity selesai supaya Overview / gating Submit langsung
      // mencerminkannya tanpa refetch penuh.
      setDetail(prev => ({
        ...prev,
        steps: { ...prev.steps, [STEP.VALIDITY]: STEP_STATUS.COMPLETED },
      }));
      toast.success('Jadwal tersimpan');
    } catch (error) {
      toast.error(`Failed to save schedule: ${error}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className='border border-info-subtle shadow-none mb-4'>
      <CardBody>
        <h6 className='fw-semibold mb-1'>
          <i className='ri-time-line align-middle me-2'></i>
          Jadwal Batas Penawaran &amp; Masa Berlaku (Format Waktu 24 Jam WIB)
        </h6>
        <p className='text-muted fs-12 mb-3'>
          Atur Tanggal dan Jam (24 Jam WIB, misal 13:00 atau 16:30) untuk
          Submission Deadline serta Validity Period penawaran vendor.
        </p>

        <Row className='g-3'>
          <Col lg={6}>
            <Label className='form-label'>
              Submission Deadline (Jakarta Local Time / WIB){' '}
              <span className='text-danger'>*</span>
            </Label>
            <DatePicker
              selected={
                validity.submissionDeadline
                  ? new Date(validity.submissionDeadline)
                  : null
              }
              onChange={d => patch({ submissionDeadline: toIso(d) })}
              showTimeSelect
              timeFormat='HH:mm'
              timeIntervals={15}
              dateFormat='dd MMM yyyy HH:mm'
              className='form-control'
              placeholderText='Select date & time'
              disabled={readOnly}
            />
            <div className='d-flex justify-content-between bg-light rounded px-2 py-1 mt-2 fs-12'>
              <span className='text-muted'>Batas Waktu Ditutup:</span>
              <span className='fw-semibold font-monospace'>
                {validity.submissionDeadline
                  ? formatDateTime(validity.submissionDeadline)
                  : '-'}
              </span>
            </div>
          </Col>
          <Col lg={6}>
            <Label className='form-label'>
              Validity Period (Jakarta Local Time / WIB){' '}
              <span className='text-danger'>*</span>
            </Label>
            <DatePicker
              selected={
                validity.validityPeriod
                  ? new Date(validity.validityPeriod)
                  : null
              }
              onChange={d => patch({ validityPeriod: toIso(d) })}
              showTimeSelect
              timeFormat='HH:mm'
              timeIntervals={15}
              dateFormat='dd MMM yyyy HH:mm'
              minDate={
                validity.submissionDeadline
                  ? new Date(validity.submissionDeadline)
                  : undefined
              }
              className='form-control'
              placeholderText='Select date & time'
              disabled={readOnly}
            />
            <div className='d-flex justify-content-between bg-light rounded px-2 py-1 mt-2 fs-12'>
              <span className='text-muted'>Masa Berlaku Berakhir:</span>
              <span className='fw-semibold font-monospace'>
                {validity.validityPeriod
                  ? formatDateTime(validity.validityPeriod)
                  : '-'}
              </span>
            </div>
          </Col>
        </Row>

        {!readOnly && (
          <div className='text-end mt-3'>
            <ActionButton
              variant='primary'
              icon='ri-save-3-line'
              loading={saving}
              onClick={handleSaveSchedule}
            >
              Simpan Perubahan Jadwal
            </ActionButton>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default ProcurementValidityTab;
